import { useEffect, useId, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  getAbonosConProblema,
  getVentasConProblema,
  reintentarAbono,
  reintentarTodas,
  reintentarVenta,
  type AbonoConProblema,
  type VentaConProblema,
} from '@/lib/sync-queue';
import { formatMoney } from '@/lib/format';
import { formatDateTime } from '@mibodega/shared-types';
import { Dialog } from './Dialog';
import { Button } from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
  onReintentar: () => void;
}

export function PendingSalesPanel({ open, onClose, onReintentar }: Props) {
  const titleId = useId();
  const descId = useId();
  const [ventas, setVentas] = useState<VentaConProblema[]>([]);
  const [abonos, setAbonos] = useState<AbonoConProblema[]>([]);

  async function reload() {
    const [nextVentas, nextAbonos] = await Promise.all([
      getVentasConProblema(),
      getAbonosConProblema(),
    ]);
    setVentas(nextVentas);
    setAbonos(nextAbonos);
  }

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    void reload()
      .then(() => {
        if (cancelado) {
          /* ignore */
        }
      })
      .catch((err) => {
        if (cancelado) return;
        toast.error(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelado = true;
    };
  }, [open]);

  async function handleReintentarUna(venta: VentaConProblema) {
    try {
      await reintentarVenta(venta.id);
      await reload();
      onReintentar();
      toast.success(`Venta ${venta.codigo} en cola de reintento`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleReintentarTodas() {
    try {
      await reintentarTodas();
      await reload();
      onReintentar();
      toast.success('Operaciones en cola de reintento');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Pendientes de sincronizar"
      titleId={titleId}
      descriptionId={descId}
      description="Ventas y abonos cobrados que todavía no llegaron al sistema central."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={handleReintentarTodas} disabled={ventas.length + abonos.length === 0}>
            Reintentar todas
          </Button>
        </div>
      }
    >
      {ventas.length + abonos.length === 0 ? (
        <p className="py-6 text-center text-sm text-neutral-500">
          No hay operaciones con problemas de sincronización.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {abonos.map((abono) => (
            <li key={abono.id} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-neutral-900">
                    Abono · {abono.clienteNombre}
                  </span>
                  <span className="tnum text-sm text-neutral-700">
                    {formatMoney(abono.monto, abono.monedaCodigo)}
                  </span>
                </div>
                <p className="text-xs text-neutral-500">{formatDateTime(abono.fecha)}</p>
                <p className="text-danger mt-1 flex items-start gap-1.5 text-xs">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 break-words">
                    {abono.estado === 'rechazada' ? 'Rechazado por el servidor' : 'Reintentando'}
                    {abono.errorMsg ? ` — ${abono.errorMsg}` : ''}
                  </span>
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  void reintentarAbono(abono.id)
                    .then(reload)
                    .then(onReintentar)
                    .then(() => toast.success('Abono en cola de reintento'))
                    .catch((err: unknown) =>
                      toast.error(err instanceof Error ? err.message : String(err)),
                    )
                }
                aria-label={`Reintentar abono de ${abono.clienteNombre}`}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Reintentar
              </Button>
            </li>
          ))}
          {ventas.map((venta) => (
            <li key={venta.id} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="tnum font-mono text-sm font-semibold text-neutral-900">
                    {venta.codigo}
                  </span>
                  <span className="tnum text-sm text-neutral-700">
                    {formatMoney(venta.total, venta.monedaCodigo)}
                  </span>
                </div>
                <p className="text-xs text-neutral-500">{formatDateTime(venta.fecha)}</p>
                <p className="text-danger mt-1 flex items-start gap-1.5 text-xs">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 break-words">
                    {venta.estado === 'rechazada'
                      ? 'Rechazada por el servidor'
                      : `Reintentando (intento ${venta.intentos})`}
                    {venta.errorMsg ? ` — ${venta.errorMsg}` : ''}
                  </span>
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => handleReintentarUna(venta)}
                aria-label={`Reintentar venta ${venta.codigo}`}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Reintentar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
